import { FastField, Field } from 'formik'
import React from 'react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'

import { graphQLClient } from '../../services/authService'
import customerQueries from '../../services/graphql/customers/queries'
import placeService from '../../services/placeService'
import { Customer } from '../../types/customer'
import { NewOfferTDO } from '../../types/offers'
import { OfferRedemptionType, OfferType } from '../../utils/config'
import { useUser } from '../../hooks/useUser'

import Card from '../Card'
import FormikAsyncSelect from '../FormikAsyncSelect'
import FormikDragZone from '../FormikDragZone'
import FormikInput from '../FormikInput'
import RadioGroup, { RadioGroupItem } from '../RadioGroup'
import FormikSelect from '../FormikSelect'
import FormikTextarea from '../FormikTextarea'
import clsx from 'clsx'

type NewOfferProps = {
  isSubmitting: boolean
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  values: NewOfferTDO
  imageUrl?: string
  qrUrl?: string
}

const typeOptions = [
  { name: 'Reward', description: 'Users will unlock this through leaving a verified experience.' },
  { name: 'Offer', description: 'Users can redeem offers using Buzzjar points.' },
]

const redemptionTypeOptions: RadioGroupItem[] = [
  {
    name: 'In Person',
    description: 'Users can redeem in person by showing the redemption screen within the app',
    value: OfferRedemptionType.InPerson,
  },
  {
    name: 'Online',
    description: 'Users can redeem via your website using a discount code',
    value: OfferRedemptionType.Online,
  },
]

const OfferForm: React.FC<NewOfferProps> = ({ isSubmitting, setFieldValue, values, imageUrl, qrUrl }) => {
  const { id } = useParams<{ id: string }>()
  const user = useUser()

  const { data: categoryOptions } = useQuery('placeCategories', () => placeService.getCategories(), {
    select: (place) =>
      place.map(({ name, id }) => ({
        value: id,
        label: name,
      })),
  })

  const { data: placeOptions } = useQuery(
    ['placesOptions', values.customer],
    () =>
      placeService.getAll({
        where: {
          customer: {
            id: values.customer?.id,
          },
        },
      }),
    {
      select: (place) =>
        place.map(({ name, id }) => ({
          value: id,
          label: name,
        })),
    }
  )

  const customerOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    const response = await graphQLClient.request(customerQueries.getAll, {
      where: { name_contains: inputValue },
    })
    return response.customers
  }

  return (
    <>
      <Card.Field col>
        {!id && (
          <fieldset disabled={isSubmitting} className="mb-6">
            <Field
              component={RadioGroup}
              name="type"
              options={typeOptions}
              select={values.type === OfferType.reward ? typeOptions[0] : typeOptions[1]}
              handleSelect={(item: RadioGroupItem) => {
                setFieldValue('type', item.name === 'Reward' ? OfferType.reward : OfferType.offer)
              }}
            />
          </fieldset>
        )}

        <fieldset disabled={isSubmitting} className="mb-6">
          <Field
            component={RadioGroup}
            name="redemptionType"
            options={redemptionTypeOptions}
            select={redemptionTypeOptions.find((option) => option.value === values.redemptionType)}
            handleSelect={(item: RadioGroupItem) => {
              setFieldValue('redemptionType', item.value)
            }}
          />
        </fieldset>

        <fieldset disabled={isSubmitting} className="mb-6">
          <Field component={FormikDragZone} label="Image*" name="image" placeholderImage={imageUrl} />
        </fieldset>

        {categoryOptions && categoryOptions.length > 0 && (
          <fieldset disabled={isSubmitting} className="mb-6">
            <Field
              name="categories"
              component={FormikSelect}
              label="Category*"
              options={categoryOptions}
              placeholder="Select a category"
            />
          </fieldset>
        )}

        <fieldset disabled={isSubmitting} className="mb-6">
          <FastField type="text" component={FormikInput} label="Title*" placeholder="Great Reward" name="title" />
        </fieldset>

        <fieldset disabled={isSubmitting} className="mb-6">
          <FastField component={FormikTextarea} label="Description*" placeholder="Description" name="description" />
        </fieldset>

        <fieldset disabled={isSubmitting} className="mb-6">
          <FastField
            component={FormikTextarea}
            label="How to Redeem*"
            placeholder="Redeem instructions"
            name="howToRedeem"
          />
        </fieldset>

        {values.type === OfferType.reward && (
          <fieldset disabled={isSubmitting} className="mb-6">
            <FastField
              component={FormikTextarea}
              label="Active Reward Message*"
              placeholder="Active Reward Message"
              name="activeMessage"
            />
          </fieldset>
        )}

        <fieldset disabled={isSubmitting} className="mb-6">
          <FastField
            type="text"
            component={FormikInput}
            label="Terms & Conditions*"
            placeholder="Terms and conditions, or a link to them"
            name="termsAndConditions"
          />
        </fieldset>

        {user.data?.type === 'BUZZL' ? (
          <fieldset disabled={isSubmitting} className="mb-6">
            <Field
              name="customer"
              component={FormikAsyncSelect}
              label="Customer*"
              loadOptions={customerOptions}
              getOptionLabel={(item: Customer) => item.name}
              getOptionValue={(item: Customer) => item.id}
              placeholder="Select Customer"
            />
          </fieldset>
        ) : null}

        {values.redemptionType === 'ONLINE' && (
          <fieldset
            disabled={isSubmitting}
            className={clsx({
              'mb-6': true, // General layout
              'rounded-lg bg-gray-50 px-6 py-6': true, // Grey sub-panel
            })}
          >
            <h3 className="mb-2 text-lg font-medium text-gray-900">Online Redemption</h3>
            <p className="mb-6 text-sm font-normal leading-5 text-gray-500">
              Both fields are optional, but allow user to redeem your offer either via URL or via a discount code that
              they can use on your website.
            </p>
            <div className="mb-6">
              <FastField
                type="url"
                component={FormikInput}
                label="URL"
                placeholder="www.example.com"
                name="onlineRedemptionUrl"
              />
            </div>

            <FastField
              type="text"
              component={FormikInput}
              label="Discount Code"
              placeholder="ABC1234"
              name="onlineRedemptionCode"
            />
          </fieldset>
        )}

        {values.customer && placeOptions && placeOptions.length > 0 && (
          <fieldset disabled={isSubmitting} className="mb-6">
            <Field
              name="places"
              component={FormikSelect}
              label="Locations"
              options={placeOptions}
              placeholder="Type Location Name"
              isMulti
            />
            <p className="mt-2 text-sm font-normal leading-5 text-gray-500">
              Selecting no locations will apply reward to entire customer portfolio.
            </p>
          </fieldset>
        )}

        {values.type === OfferType.reward && (
          <fieldset disabled={isSubmitting} className="mb-6">
            <Field component={FormikDragZone} label="Redemption QR Code" name="qrCode" placeholderImage={qrUrl} />
            <p className="mt-2 text-sm font-normal leading-5 text-gray-500">
              Allow customers to validate a reward using their own QR codes.
            </p>
          </fieldset>
        )}
      </Card.Field>
      <Card.Field>Values</Card.Field>
      <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
        <div className="py-4 sm:grid sm:grid-cols-2 sm:gap-5 sm:py-5 sm:px-6">
          <fieldset disabled={isSubmitting}>
            <FastField
              type="number"
              component={FormikInput}
              label="Max Claimable"
              placeholder="--"
              name="maxClaimable"
            />
            <p className="mt-2 text-sm font-normal leading-5 text-gray-500">Leave blank to disable limit.</p>
          </fieldset>
          <fieldset disabled={isSubmitting}>
            <FastField
              type="number"
              component={FormikInput}
              label="Limit per Customer"
              placeholder="--"
              name="nbPermittedClaims"
            />
            <p className="mt-2 text-sm font-normal leading-5 text-gray-500">Leave blank to disable limit.</p>
          </fieldset>

          <fieldset disabled={isSubmitting || (user.data?.type === 'CUSTOMER' && user.data?.role === 'OPERATOR')}>
            <FastField type="date" component={FormikInput} label="Earn From*" name="earnableFrom" />
            <p className="mt-2 text-sm font-normal leading-5 text-gray-500">Start at 0:00:01 am on this date.</p>
          </fieldset>
          <fieldset disabled={isSubmitting || (user.data?.type === 'CUSTOMER' && user.data?.role === 'OPERATOR')}>
            <FastField type="date" component={FormikInput} label="Earn Until*" name="earnableTo" />
            <p className="mt-2 text-sm font-normal leading-5 text-gray-500">End at 0:00:01 am on this date.</p>
          </fieldset>
          <fieldset disabled={isSubmitting || (user.data?.type === 'CUSTOMER' && user.data?.role === 'OPERATOR')}>
            <FastField type="date" component={FormikInput} label="Redemption From*" name="redeemableFrom" />
            <p className="mt-2 text-sm font-normal leading-5 text-gray-500">Start at 0:00:01 am on this date.</p>
          </fieldset>
          <fieldset disabled={isSubmitting || (user.data?.type === 'CUSTOMER' && user.data?.role === 'OPERATOR')}>
            <FastField type="date" component={FormikInput} label="Redemption Until*" name="redeemableTo" />
            <p className="mt-2 text-sm font-normal leading-5 text-gray-500">End at 0:00:01 am on this date.</p>
          </fieldset>
        </div>
        {values.type === OfferType.offer && (
          <div className="pb-4 sm:px-6">
            <fieldset disabled={isSubmitting} className="mb-6">
              <FastField
                type="number"
                component={FormikInput}
                label="Cost (Buzzjar Points)*"
                placeholder="0"
                name="cost"
              />
            </fieldset>
          </div>
        )}
      </div>
    </>
  )
}

export default OfferForm
